import {html, LitElement} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import styles from './g-tab.component.scss?as=stylesheet';
import {defineCustomElement} from 'extensions/utils';

class GenesisTab extends LitElement {
	static styles = [styles];

	static get properties () {
		return {
			controls: {
				type: String
			},
			label: {
				type: String
			},
			open: {
				type: Boolean
			}
		};
	}

	constructor () {
		super();
		this.controls = '';
		this.label = '';
		this.open = false;
		this.tabIndex = 0;

		// Bind event handlers
		this.handleClick = this._handleClick.bind(this);
		this.handleKeyup = this._handleKeyup.bind(this);

		// Set default aria attributes
		this.setAttribute('role', 'tab');
	}

	/**
	 * Helper method to dispatch the custom tab:open event. Used for DRY purposes.
	 */
	dispatchOpenEvent () {
		this.dispatchEvent(new CustomEvent(`tab:open`, {
			bubbles: true,
			composed: true,
			detail: {
				controls: this.controls
			}
		}));
	}

	dispatchKeyupEvent (direction) {
		this.dispatchEvent(new CustomEvent('tab:keyup', {
			bubbles: true,
			composed: true,
			detail: {
				direction
			}
		}));
	}

	/*
	 *  Open the tab on click of the outer <g-tab> tag.
	 */
	_handleClick (e) {
		this.dispatchOpenEvent(e.type);
	}

	/*
	 *  Open the tab on Enter/Space of the outer <g-tab> tag.
	 */
	_handleKeyup (e) {
		switch (e.code) {
			case 'Space':
			case 'Enter':
			case 'NumpadEnter': {
				this.dispatchOpenEvent();
				break;
			}
			case 'ArrowRight': {
				this.dispatchKeyupEvent('right');
				break;
			}
			case 'ArrowLeft': {
				this.dispatchKeyupEvent('left');
				break;
			}
			default:
				break;
		}
	}

	connectedCallback () {
		super.connectedCallback();

		this.addEventListener('click', this.handleClick);
		this.addEventListener('keyup', this.handleKeyup);
	}

	updated (changedProperties) {
		if (changedProperties.has('open')) {
			this.ariaSelected = this.open;
			this.setAttribute('aria-selected', this.open);
			this.tabIndex = this.open ? 0 : -1;
		}

		if (changedProperties.has('label')) {
			this.ariaLabel = `Tab: ${this.label}`;
			this.setAttribute('aria-label', this.ariaLabel);
		}

		if (changedProperties.has('controls')) {
			this.ariaControls = this.controls;
			this.setAttribute('aria-controls', this.controls);
			this.id = `tab-${this.controls}`;
		}
	}

	render () {
		const rootClasses = {
			container: true,
			'is--open': this.open
		};

		return html`
			<div class="${classMap(rootClasses)}">
				<div class="inner-text">
					${this.label}
				</div>
			</div>
		`;
	}
};

defineCustomElement('g-tab', GenesisTab);
